/**
 * The daily-peak series behind the Existing Load chart and the report figure.
 *
 * Built from the same readings the calculation uses. Readings are stored as
 * fixed-UTC wall clock (the meter's own clock written with a Z; see
 * `utcWallClock` in fileParser.ts), so days, hours and the peak label are all
 * read in UTC, never the browser's zone: a peak at 6:00 PM on the meter shows
 * as 6:00 PM wherever the report is opened, matching the Results tab and the
 * report to the minute. Nothing is stored; this is derived on every render.
 */

export interface DemandPoint {
  timestamp: string;
  kw: number;
}

export interface DemandDay {
  /** Meter calendar date (UTC wall clock), YYYY-MM-DD. */
  date: string;
  /** Highest reading that day. */
  max: number;
  /** Timestamp of that reading. */
  maxTimestamp: string;
  /** Readings that day. */
  n: number;
}

export interface DemandGap {
  /** Meter date the gap starts on. */
  date: string;
  minutes: number;
}

export interface DemandMonth {
  /** YYYY-MM. */
  key: string;
  /** Index range into `days`, end exclusive. */
  start: number;
  end: number;
}

export interface DemandSeries {
  days: DemandDay[];
  peakIndex: number;
  peak: {
    kw: number;
    date: string;
    timestamp: string;
    /** "5:30 AM" */
    timeLabel: string;
    season: Season;
  };
  /** Readings on the peak's meter calendar day; hour is fractional meter time. */
  peakDay: Array<{ hour: number; kw: number }>;
  gaps: DemandGap[];
  months: DemandMonth[];
  seasons: Season[];
  /** Readings counted after trimming partial edge days. */
  readings: number;
  intervalMinutes: number;
  /** A clean axis ceiling and tick step for the chart. */
  ymax: number;
  step: number;
}

export type Season = 'Winter' | 'Spring' | 'Summer' | 'Fall';

const pad = (n: number) => String(n).padStart(2, '0');
/** Meter calendar date of a stored instant. Exported so the chart keys excluded windows the same way. */
export const meterDateKey = (ms: number) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

export function seasonOfDate(date: string): Season {
  const m = Number(date.slice(5, 7));
  if (m === 12 || m <= 2) return 'Winter';
  if (m <= 5) return 'Spring';
  if (m <= 8) return 'Summer';
  return 'Fall';
}

function inferInterval(sortedMs: number[]): number {
  if (sortedMs.length < 2) return 15;
  const diffs: number[] = [];
  for (let i = 1; i < Math.min(sortedMs.length, 500); i++) diffs.push((sortedMs[i] - sortedMs[i - 1]) / 60000);
  diffs.sort((a, b) => a - b);
  const median = diffs[Math.floor(diffs.length / 2)];
  return median > 0 ? Math.round(median) : 15;
}

/**
 * Null when there is nothing to draw: no readings, or too few to make a day.
 * Partial days at either edge of the export (a file that starts mid-afternoon)
 * are trimmed so the first and last days on the chart are whole ones.
 */
export function buildDemandSeries(
  points: DemandPoint[],
  intervalMinutes?: number | null
): DemandSeries | null {
  if (!points || points.length === 0) return null;

  // One reading per instant, the higher kept — the merge already did this, but
  // the series must not depend on it.
  const byMs = new Map<number, { kw: number; timestamp: string }>();
  for (const p of points) {
    const ms = new Date(p.timestamp).getTime();
    if (!Number.isFinite(ms) || !Number.isFinite(p.kw)) continue;
    const cur = byMs.get(ms);
    if (!cur || p.kw > cur.kw) byMs.set(ms, { kw: p.kw, timestamp: p.timestamp });
  }
  const sortedMs = [...byMs.keys()].sort((a, b) => a - b);
  if (sortedMs.length === 0) return null;

  const interval = intervalMinutes && intervalMinutes > 0 ? intervalMinutes : inferInterval(sortedMs);
  const perDay = Math.max(1, Math.round(1440 / interval));

  const dayMap = new Map<string, DemandDay>();
  for (const ms of sortedMs) {
    const r = byMs.get(ms)!;
    const key = meterDateKey(ms);
    const d = dayMap.get(key);
    if (!d) dayMap.set(key, { date: key, max: r.kw, maxTimestamp: r.timestamp, n: 1 });
    else {
      d.n += 1;
      if (r.kw > d.max) { d.max = r.kw; d.maxTimestamp = r.timestamp; }
    }
  }
  let days = [...dayMap.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
  // Trim partial edge days, but never below one day.
  while (days.length > 1 && days[0].n < perDay * 0.5) days = days.slice(1);
  while (days.length > 1 && days[days.length - 1].n < perDay * 0.5) days = days.slice(0, -1);
  if (days.length === 0) return null;

  let peakIndex = 0;
  for (let i = 1; i < days.length; i++) if (days[i].max > days[peakIndex].max) peakIndex = i;
  const peakDayRec = days[peakIndex];
  const peakDate = new Date(peakDayRec.maxTimestamp);
  const dayStart = Date.UTC(peakDate.getUTCFullYear(), peakDate.getUTCMonth(), peakDate.getUTCDate());
  const dayEnd = dayStart + 86400000;
  const peakDay: Array<{ hour: number; kw: number }> = [];
  for (const ms of sortedMs) {
    if (ms < dayStart) continue;
    if (ms >= dayEnd) break;
    peakDay.push({ hour: (ms - dayStart) / 3600000, kw: byMs.get(ms)!.kw });
  }

  const firstDay = days[0].date, lastDay = days[days.length - 1].date;
  const gaps: DemandGap[] = [];
  for (let i = 1; i < sortedMs.length; i++) {
    const minutes = (sortedMs[i] - sortedMs[i - 1]) / 60000;
    if (minutes > interval * 2) {
      const date = meterDateKey(sortedMs[i - 1]);
      if (date >= firstDay && date <= lastDay) gaps.push({ date, minutes: Math.round(minutes) });
    }
  }

  const months: DemandMonth[] = [];
  days.forEach((d, i) => {
    const key = d.date.slice(0, 7);
    if (!months.length || months[months.length - 1].key !== key) months.push({ key, start: i, end: i + 1 });
    else months[months.length - 1].end = i + 1;
  });

  const seasonSet = new Set<Season>();
  for (const d of days) seasonSet.add(seasonOfDate(d.date));
  const order: Season[] = ['Winter', 'Spring', 'Summer', 'Fall'];

  const maxKw = peakDayRec.max;
  const step = maxKw > 12 ? 5 : maxKw > 4 ? 2 : 1;
  const ymax = Math.max(step, Math.ceil((maxKw * 1.08) / step) * step);

  return {
    days,
    peakIndex,
    peak: {
      kw: maxKw,
      date: peakDayRec.date,
      timestamp: peakDayRec.maxTimestamp,
      timeLabel: peakDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }),
      season: seasonOfDate(peakDayRec.date),
    },
    peakDay,
    gaps,
    months,
    seasons: order.filter(s => seasonSet.has(s)),
    readings: days.reduce((s, d) => s + d.n, 0),
    intervalMinutes: interval,
    ymax,
    step,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dateOf(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** "Jan 11" */
export function formatMonthDay(date: string): string {
  const d = dateOf(date);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Jan 11, 2025" */
export function formatMonthDayYear(date: string): string {
  const d = dateOf(date);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Sat, Jan 11" */
export function formatDowMonthDay(date: string): string {
  const d = dateOf(date);
  return `${DOW[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Saturday, January 11, 2025" */
export function formatLongDate(date: string): string {
  return dateOf(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
